import React, { useMemo, useState } from 'react'
import { Pagination, Table } from 'antd'
import styled from 'styled-components'
import { CommonConstant, DateFormat, DateUtility } from 'utility'
import { CaretUp, CaretDown } from 'phosphor-react'
import { StyledCheckbox } from './StyleCloseIcon'
import { LoaderBar } from './LoaderBar'

const StyledTableContainer = styled.div`
  padding-top: 16px;

  .table-wrapper {
    max-height: 100vh;
    overflow-y: auto;
  }

  .ant-table-thead > tr,
  .ant-table-tbody > tr {
    font-size: 12px;
    border-bottom: 1px solid #b3b3b3 !important;
  }

  .ant-table-thead > tr > th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${({ theme }) => theme.colors.white};
  }

  @media (max-width: 1350px) {
    .ant-table-thead > tr > th {
      font-size: 12px;
    }
  }
  .ant-table-tbody > tr:not(:last-child) > td {
    border-bottom: 1px solid #b3b3b3 !important;
  }

  .custom-table {
    border-radius: 10px;
    overflow: auto;
    border: 1px solid #b3b3b3;
    scrollbar-width: thin;
    scrollbar-color: #f5f7ff transparent;

    .ant-table-content,
    .ant-table-body {
      overflow: auto !important;
      scrollbar-width: thin;
      scrollbar-color: #b0b0b0 transparent;
    }

    .ant-table .ant-table-content::-webkit-scrollbar,
    .ant-table .ant-table-body::-webkit-scrollbar {
      width: 2px !important;
      height: 2px !important;
    }

    .ant-table-content::-webkit-scrollbar-track,
    .ant-table-body::-webkit-scrollbar-track {
      background: transparent;
      border-radius: 10px !important;
    }

    .ant-table-content::-webkit-scrollbar-thumb,
    .ant-table-body::-webkit-scrollbar-thumb {
      background: #b5c6ff;
      border-radius: 10px !important;
    }

    .ant-table-content::-webkit-scrollbar-thumb:hover,
    .ant-table-body::-webkit-scrollbar-thumb:hover {
      background: red !important;
    }
    .gold-row {
      background-color: #faf7ea !important; /* Light gold */
    }

    .silver-row {
      background-color: #f2f2f2 !important; /* Light silver */
    }

    .bronze-row {
      background-color: #faf2ea !important; /* Light bronze */
    }
  }
`
const StyledPagination = styled(Pagination)`
  .ant-pagination-item {
    background-color: #f0f0f0;
    border-radius: 4px;
    border: none;
  }

  .ant-pagination-item a {
    color: #333;
  }

  .ant-pagination-item-active {
    background-color: ${({ theme }) => theme.colors.primary};
  }

  .ant-pagination-item-active a {
    color: white !important;
  }
`

export const CustomTable = ({
  columns,
  data = [],
  pageChanged,
  page,
  updateFilter,
  loading,
  pageSize,
  selected,
  total,
  onAllSelect,
  pageSizeChanged,
  hideIndex,
  hideSorting,
  rankings, // Flag to enable row styling
}) => {
  const [checked, setChecked] = useState(false)
  const [isAscending, setIsAscending] = useState(true)
  const [key, setKey] = useState(null)
  const handleSort = ele => {
    updateFilter({ sortBy: ele?.key, sortOrder: isAscending ? 'desc' : 'asc' })
    setIsAscending(!isAscending)
    setKey(ele?.key)
  }
  const columnsList = useMemo(() => {
    const updatedColumns = columns?.map(ele => {
      let column = { ...ele }
      column = {
        ...column,
        title: () => (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              gap: '10px',
            }}
          >
            {ele.title === 'Flag' ? (
              <StyledCheckbox
                checked={selected?.length === data.length && data.length > 0}
                onClick={() => {
                  setChecked(!checked)
                  onAllSelect(!checked)
                }}
              />
            ) : (
              <>
                <span>{ele.title}</span>
                {!hideSorting && (
                  <>
                    {ele?.title !== 'Birth Date' &&
                      ele?.title !== 'Action' &&
                      ele?.title !== 'Phone Number' && (
                        <span
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: 'pointer',
                            lineHeight: '0.8',
                          }}
                          onClick={() => handleSort(ele)}
                        >
                          <CaretUp
                            size={10}
                            weight="bold"
                            color={
                              isAscending && key === ele?.key ? 'blue' : 'gray'
                            }
                          />
                          <CaretDown
                            size={10}
                            weight="bold"
                            color={
                              !isAscending && key === ele?.key ? 'blue' : 'gray'
                            }
                          />
                        </span>
                      )}
                  </>
                )}
              </>
            )}
          </span>
        ),
      }

      if (ele.date) {
        column = {
          ...column,
          render: text => (
            <>
              {DateUtility.dateToLocalISO(new Date(text), DateFormat.DDMMYYYY)}
            </>
          ),
        }
      }

      return column
    })

    const indexColumn = {
      title: 'No.',
      dataIndex: 'index',
      key: 'index',
      width: 40,
      render: (_, __, rowIndex) => page * pageSize + (rowIndex + 1),
    }

    return [...(!hideIndex ? [indexColumn] : []), ...updatedColumns]
  }, [columns, page, pageSize])
  return (
    <StyledTableContainer>
      <>
        <div className="table-wrapper">
          <Table
            className="custom-table"
            columns={columnsList}
            dataSource={data || []}
            size="small"
            pagination={false}
            // loading={loading}
            loading={{
              spinning: loading,
              indicator: <LoaderBar height="120px" content="Loading..." />,
            }}
            rowClassName={(record, index) => {
              if (!rankings) return ''
              if (index === 0) return 'gold-row'
              if (index === 1) return 'silver-row'
              if (index === 2) return 'bronze-row'
              return ''
            }}
            sticky
            scroll={{ y: 400 }}
            style={{ width: '100%' }}
          />
        </div>
      </>
      <div className="mt-4 d-flex justify-content-end">
        <CommonPagination
          page={page}
          pageSize={pageSize}
          total={total}
          pageChanged={pageChanged}
          pageSizeChanged={pageSizeChanged}
        />
      </div>
    </StyledTableContainer>
  )
}

export const CommonPagination = ({
  page,
  pageSize,
  total,
  pageChanged,
  pageSizeChanged,
}) => {
  return (
    <StyledPagination
      current={page + 1}
      pageSize={pageSize || CommonConstant.defaultPageSize}
      total={total}
      onChange={page => pageChanged(page - 1)}
      showTotal={total => `Total : ${total}`}
      onShowSizeChange={(_, size) => pageSizeChanged(size)}
    />
  )
}
